/**
 * Phase 18 M13 / Phase 19 F21: whether an RN may start a supervisory visit for a patient.
 * Phase 20C: a PT, OT or ST may too, for their assistants (and PT / OT for a therapy-only aide).
 * Offered when the patient has LVN or HHA staff on the care team or on the schedule
 * (30 days back to 30 days ahead). The server makes the final check.
 */
import { fetchPatientProviderAssignments } from '../admin/patient.service';
import { ScheduleService } from '../scheduling';
import { disciplineEntries, getDisciplineEntry, getProviderTypeEntry, providerTypesForDiscipline, isAssistantProviderType } from '../../constants/disciplines';

/** Phase 20D: aide-type disciplines (HHA, ATTENDANT), supervised by an RN, or a PT / OT for a therapy-only aide */
const aideTypes = (): string[] => disciplineEntries().filter((d) => d.aideTasks).map((d) => d.code);

/** Disciplines an RN supervises (LPN is the older LVN name) */
const SUPERVISED = new Set(['LVN', 'LPN', 'HHA']);

/**
 * Phase 20C: a licensed RN, PT, OT or ST leads supervisory visits (never an LVN, assistant or aide).
 * The server makes the same check (disciplines.isSupervisingProviderType).
 */
export const isSupervisingClinician = (providerType: string | null | undefined): boolean => {
  const entry = getProviderTypeEntry(providerType);
  return Boolean(entry && entry.role === 'licensed' && !entry.limitedNursing && getDisciplineEntry(entry.discipline)?.skilled);
};

/** Phase 20C: whom a supervisor supervises: an RN its LVNs and aides; a therapist its assistants (PT / OT also a therapy-only aide) */
export const supervisedByType = (providerType: string | null | undefined): Set<string> => {
  const entry = getProviderTypeEntry(providerType);
  if (!entry) return new Set();
  if (entry.discipline === 'RN') return new Set([...SUPERVISED, ...aideTypes()]);
  const assistants = providerTypesForDiscipline(entry.discipline).filter((t) => isAssistantProviderType(t));
  return new Set([...assistants, ...(getDisciplineEntry(entry.discipline)?.supervisesAides ? aideTypes() : [])]);
};
const WINDOW_DAYS = 30;

const norm = (d: string | null | undefined) => String(d || '').trim().toUpperCase();

/** Only a registered nurse starts a supervisory visit */
export const isRnClinician = (providerType: string | null | undefined): boolean => norm(providerType) === 'RN';

/** True when any discipline in the list is supervised by an RN (or by `supervisorType`, Phase 20C) */
export const includesSupervisedDiscipline = (disciplines: Array<string | null | undefined>, supervisorType = 'RN'): boolean => {
  const supervised = supervisedByType(supervisorType);
  return disciplines.some((d) => supervised.has(norm(d)));
};

const shiftDate = (ymd: string, days: number): string => {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

export class SupervisionService {
  /**
   * LVN or HHA staff on the patient's care team (active assignments) or scheduled shifts.
   * Either source may fail (permissions, offline); the other still counts. Never throws.
   */
  static async patientHasSupervisedStaff(patientId: string, today: string, supervisorType = 'RN'): Promise<boolean> {
    const [team, shifts] = await Promise.allSettled([
      fetchPatientProviderAssignments(patientId),
      ScheduleService.listByPatient(patientId, shiftDate(today, -WINDOW_DAYS), shiftDate(today, WINDOW_DAYS)),
    ]);
    const disciplines: Array<string | null | undefined> = [];
    if (team.status === 'fulfilled') {
      for (const a of team.value) {
        if (a.status && a.status !== 'active') continue;
        disciplines.push(a.provider?.discipline, a.assignmentType);
      }
    }
    if (shifts.status === 'fulfilled') {
      for (const s of shifts.value) {
        if (String(s.status || '').toUpperCase() === 'CANCELLED') continue;
        disciplines.push(s.provider?.discipline, s.discipline);
      }
    }
    return includesSupervisedDiscipline(disciplines, supervisorType);
  }
}

export default SupervisionService;
